import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../utils';
import { Icon } from './Icon';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Added 'skills' to ensure full site navigation
  const navItems = [
    { id: 'hero', label: './HOME' },
    { id: 'about', label: './About' },
    { id: 'experience', label: './Experience' },
    { id: 'projects', label: './Projects' },
    { id: 'skills', label: './Skills' },
    { id: 'contact', label: './CONTACT' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId: string) => {
    scrollToSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#050505]/95 backdrop-blur-md border-b border-zinc-900' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-8 py-5">
        
        {/* Minimalist Terminal Anchor (Name Removed) */}
        <div 
          onClick={() => handleNavClick('hero')}
          className="text-zinc-500 font-mono text-sm tracking-widest font-bold select-none cursor-pointer hover:text-white transition-colors"
        >
          {'>_'}
        </div>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex gap-6 lg:gap-8 list-none">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => handleNavClick(item.id)}
                className="text-zinc-400 font-mono text-xs uppercase tracking-widest py-2 transition-colors duration-300 hover:text-white"
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors"
          aria-label="Toggle menu"
        >
          <Icon 
            name={isMobileMenuOpen ? 'close' : 'menu'} 
            size={24}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-[#0a0a0a] border-b border-zinc-900 shadow-2xl"
          >
            <ul className="flex flex-col py-2 px-4 sm:px-8">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className="w-full text-left text-zinc-400 font-mono text-xs uppercase tracking-widest py-4 border-b border-zinc-900/50 hover:text-white hover:bg-zinc-900/30 transition-all pl-2"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Header;